import type * as Highcharts from 'highcharts';

/** Max raster size so browser canvas stays within typical limits. */
export const CHART_EXPORT_MAX_WIDTH = 5600;
export const CHART_EXPORT_MAX_HEIGHT = 3600;

function resolveChartType(opts: Highcharts.Options | undefined): string {
  const t = String(opts?.chart?.type || '').toLowerCase();
  if (t) {
    return t;
  }
  const s0 = opts?.series?.[0] as { type?: string } | undefined;
  return String(s0?.type || '').toLowerCase();
}

function getCategoryCount(opts: Highcharts.Options | undefined): number {
  if (!opts) {
    return 0;
  }
  const xa = opts.xAxis;
  if (Array.isArray(xa)) {
    const cats = xa[0]?.categories;
    return Array.isArray(cats) ? cats.length : 0;
  }
  if (xa && typeof xa === 'object' && Array.isArray((xa as { categories?: unknown }).categories)) {
    return (xa as { categories: unknown[] }).categories.length;
  }
  let maxLen = 0;
  for (const s of opts.series || []) {
    const d = (s as { data?: unknown }).data;
    if (Array.isArray(d)) {
      maxLen = Math.max(maxLen, d.length);
    }
  }
  return maxLen;
}

function formatCompactDataLabelValue(y: number): string {
  if (!isFinite(y)) {
    return '';
  }
  if (y === 0) {
    return '0';
  }
  const abs = Math.abs(y);
  if (abs >= 1_000_000) {
    const v = y / 1_000_000;
    const s = (abs >= 10_000_000 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, '');
    return `${s}M`;
  }
  if (abs >= 1000) {
    const v = y / 1000;
    const s = (abs >= 100_000 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, '');
    return `${s}k`;
  }
  return String(Math.round(y));
}

/**
 * Options merged into Highcharts.Chart#getSVG so exported PNG/SVG has enough room for
 * category labels and data labels, shows every label (no overlap hiding), short numeric
 * captions, and a minimum visible bar height for small values.
 */
export function buildHighchartsGetSvgExportOptions(
  chartOptions: Highcharts.Options | undefined
): Highcharts.Options {
  const base: Highcharts.Options = {
    chart: {
      backgroundColor: '#ffffff',
      spacingBottom: 20,
      spacingTop: 20,
      spacingLeft: 20,
      spacingRight: 20,
    },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
  };

  const chartType = resolveChartType(chartOptions);
  if (chartType !== 'column' && chartType !== 'bar') {
    return base;
  }

  const n = getCategoryCount(chartOptions);
  const width = Math.min(4800, Math.max(1180, Math.max(n, 1) * 54));
  const height = Math.min(2800, Math.max(640, 560 + Math.min(n, 28) * 14));

  const dataLabels: Highcharts.PlotSeriesDataLabelsOptions = {
    enabled: true,
    allowOverlap: true,
    crop: false,
    overflow: 'allow',
    style: {
      fontSize: '10px',
      fontWeight: 'normal',
      color: '#333333',
      textOutline: '1px contrast',
    },
    formatter: function (this: Highcharts.PointLabelObject) {
      const y = this.y;
      return typeof y === 'number' ? formatCompactDataLabelValue(y) : '';
    },
  };

  const plotKey = chartType === 'bar' ? 'bar' : 'column';

  return {
    ...base,
    chart: {
      ...base.chart,
      width,
      height,
    },
    plotOptions: {
      series: {
        dataLabels: {
          ...dataLabels,
        },
      },
      [plotKey]: {
        minPointLength: 4,
        dataLabels: {
          ...dataLabels,
        },
      },
    } as Highcharts.PlotOptions,
  };
}

/**
 * Scale chart raster to fit canvas limits; reserve a top band for a title row.
 */
export function fitChartExportCanvas(
  naturalWidth: number,
  naturalHeight: number,
  headerPx: number
): { canvasWidth: number; canvasHeight: number; drawWidth: number; drawHeight: number } {
  const nw = naturalWidth > 0 ? naturalWidth : 1200;
  const nh = naturalHeight > 0 ? naturalHeight : 700;
  const scale = Math.min(1, CHART_EXPORT_MAX_WIDTH / nw, CHART_EXPORT_MAX_HEIGHT / nh);
  const drawWidth = Math.max(1, Math.round(nw * scale));
  const drawHeight = Math.max(1, Math.round(nh * scale));
  return {
    canvasWidth: drawWidth,
    canvasHeight: drawHeight + headerPx,
    drawWidth,
    drawHeight,
  };
}
